<template>
  <div class="modal fade" 
      :id="`${tag || 'data'}FormModal`" 
      tabindex="-1" 
      aria-labelledby="dataFormModalTitle" 
      @click.self="closeModal"
      ref="modalRef"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="dataFormModalTitle">{{ msg }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form>
            <template v-for="item in schema" :key="item.name">
              <div class="row m-1" v-if="item.name !== 'isLocked' || isAdmin">
                <label class="col-4 col-form-label">
                  {{ item.label }}
                  <span v-if="item.required || item.primaryKey" class="text-danger">*</span>
                </label>
                <div class="col">
                  <!-- Dropdown for types defined in schemaTypes -->
                  <div v-if="availableSchemaTypes[item.type]">
                    <select 
                      class="form-select form-select-sm" 
                      v-model="state.editData[item.name]" 
                      :disabled="isViewOnly() || item.isReadOnly"
                    >
                      <option :value="undefined" disabled>Select {{ item.label }}...</option>
                      <option v-for="option in availableSchemaTypes[item.type]" :value="option" :key="option">
                        {{ option }}
                      </option>
                    </select>
                  </div>
                  <!-- JSON input -->
                  <div v-else-if="item.type.toLowerCase() === 'json'">
                    <div class="input-group input-group-sm">
                      <textarea
                        class="form-control font-monospace" 
                        v-model="state.editData[item.name]" 
                        :disabled="isViewOnly() || item.isReadOnly"
                        placeholder='{ "key": "value" }'
                        rows="8"
                        style="font-size: 0.8rem; white-space: pre;"
                      />
                      <!-- Upload Button positioned to the side -->
                      <div v-if="!isViewOnly()" class="d-flex flex-column border-start">
                        <label 
                          class="btn btn-outline-secondary border-0 flex-grow-1 d-flex align-items-center" 
                          :for="`file-${item.name}`"
                          :disabled="isViewOnly() || item.isReadOnly"
                        >
                          <i class="bi bi-upload"></i>
                        </label>
                        <input 
                          type="file" 
                          class="d-none" 
                          :id="`file-${item.name}`" 
                          accept=".json"
                          @change="(e) => handleFileUpload(e, item.name)"
                          :disabled="isViewOnly() || item.isReadOnly"
                        />
                      </div>
                    </div>
                    <small v-if="!isViewOnly()" class="text-muted" style="font-size: 0.7rem;">
                      Manual edit or upload .json
                    </small>
                  </div>
                  <!-- Standard Input -->
                  <div v-else>
                    <input 
                      class="form-control form-control-sm" 
                      v-model="state.editData[item.name]" 
                      :disabled="isViewOnly() || item.isReadOnly || (item.primaryKey && state.type === 'edit')"
                      :placeholder="parentObj[item.name] || item.type"
                      :class="{ 'bg-light': item.isReadOnly }" 
                    />
                  </div>
                </div>
              </div>
            </template>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button type="button" class="btn btn-primary" @click="saveObj" v-if="!isViewOnly()">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Generic database form information manipulation
// schema must have keys: name, type
// schema optional validation: nonNull, 

import { reactive, ref, onMounted, onUnmounted, computed } from 'vue';
import { Modal } from 'bootstrap';
import { useAuthStore } from '@/stores/authStore';

const emit = defineEmits(['formDataSubmitted', 'formClosed']);
const modalRef = ref(null);

const props = defineProps({
  tag: String,   // id prefix for FormModal div
  msg: String,   // title/id to use in modal header
  parentObj: Object,   // reactive object from parent representing a data row
  schema: Object,   // schema used to build the form inputs, placeholders and validation
  schemaTypes: Object, // types specified in schema for use in select fields
  enforceFocus: { type: Boolean, default: true },
});

const auth = useAuthStore();
const isAdmin = computed(() => auth.role === 'ADMIN');

const state = reactive({
  modalMounted: null,
  editData: {},
  type: null,
});

const availableSchemaTypes = computed(() => {
  return props.schemaTypes && props.schemaTypes.value
      ? props.schemaTypes.value
      : props.schemaTypes;
});

onMounted(() => {
  if (modalRef.value) {
    state.modalMounted = new Modal(modalRef.value, {
      focus: props.enforceFocus, 
      backdrop: 'static',
      keyboard: true,
    });
  }
  const dataClone = JSON.parse(JSON.stringify(props.parentObj));
  if (Array.isArray(props.schema)) {
    props.schema.forEach(item => {
      if (item.type.toLowerCase() === 'json' && dataClone[item.name]) {
        // Check if it's already an object (not a string)
        if (typeof dataClone[item.name] === 'object') {
          dataClone[item.name] = JSON.stringify(dataClone[item.name], null, 2);
        }
      }
    });
  }
  state.editData = dataClone;
  state.type = props.msg.split(' ')[0].toLowerCase();
  //console.debug(`Mounted: ${JSON.stringify(state.editData)}`);
  openModal();
});

function openModal() {
  if (state.modalMounted) state.modalMounted.show();
  // console.debug(`Schema: ${JSON.stringify(props.schema)}`);
}

// Helper to handle the actual closing after animation
function hideAndNotify(callback) {
  if (modalRef.value && state.modalMounted) {
    // Listen for Bootstrap's animation complete event natively on the live DOM node
    modalRef.value.addEventListener('hidden.bs.modal', () => {
      callback(); 
    }, { once: true, passive: true });
    state.modalMounted.hide();
  } else {
    // Safety Fallback: If things are already unmounted, execute the callback instantly
    callback();
  }
}

function closeModal() {
  if (document.activeElement && typeof document.activeElement.blur === 'function') {
    document.activeElement.blur();
  }
  hideAndNotify(() => emit('formClosed'));
}

function isViewOnly(){
  return state.type === 'view';
}

async function handleFileUpload(event, fieldName) {
  const file = event.target.files[0];
  if (!file) return;
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const fileHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      // Validate that it's actually JSON before saving
      const json = JSON.parse(e.target.result);
      // Store it back as a string or object depending on what your API expects
      state.editData[fieldName] = JSON.stringify(json, null, 2);
      if (fieldName === 'codecJson') {
        state.editData.data = btoa(e.target.result);
        state.editData.fileName = file.name;
        state.editData.fileHash = fileHash;
        state.editData.file = file;
      }
    } catch (err) {
      alert(`Invalid JSON file: ${err.message}`);
    }
  };
  reader.readAsText(file);
}

async function saveObj() {
  console.warn(`TODO: DataForm validation...`);
  for (const [k, v] of Object.entries(state.editData)) {
    if (v === null) delete state.editData[k];
  }
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  hideAndNotify(() => emit('formDataSubmitted', state.editData));
  // state.modalMounted.hide();
  // emit('formDataSubmitted', state.editData);
}

onUnmounted(() => {
  if (state.modalMounted) {
    state.modalMounted.dispose();
    state.modalMounted = null;
    // Force remove backdrop if Bootstrap gets stuck
    document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('paddingRight');
  }
});

</script>